'use client';

import type { CheckboxProps, RadioGroupItemProps } from '../../ui';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { cva } from 'class-variance-authority';

import { Checkbox, Label, RadioGroupItem } from '../../ui';
import { Typography } from '../typography';

type RadioOptionSelectorProps = RadioGroupItemProps & {
  as: 'radio';
  withIndicator?: boolean;
};

type CheckboxOptionSelectorProps = Omit<CheckboxProps, 'variant'> & {
  as: 'checkbox';
  withIndicator?: never;
};

export const optionSelectorSizes = ['sm', 'md', 'lg'] as const;
export const variant = ['default', 'simple'] as const;

const variants = cva(
  // `relative` anchors the sr-only (absolutely-positioned) radio to the label;
  // without it the radio resolves against <body> and drifts out of view in
  // scrolled containers, making focus-on-click scroll the page.
  'relative flex cursor-pointer leading-5 transition-colors hover:bg-accent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
  {
    variants: {
      size: {
        sm: 'min-w-28 px-6 py-2',
        md: 'min-h-11 min-w-36 px-8 py-2',
        lg: 'min-h-16 min-w-48 p-6',
      },
      variant: {
        default:
          'items-center rounded-md border border-border has-[[data-state=checked]]:border-2 has-[[data-state=checked]]:border-primary',
        simple: 'items-start',
      },
      disabled: {
        true: 'bg-muted',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
    compoundVariants: [
      {
        variant: 'default',
        size: 'lg',
        class: 'items-start',
      },
      {
        variant: 'simple',
        size: 'sm',
        class: 'h-auto min-w-24 px-4 py-0 hover:bg-transparent',
      },
      {
        variant: 'simple',
        size: 'md',
        class: 'h-auto min-w-24 px-4 py-0 hover:bg-transparent',
      },
      {
        variant: 'simple',
        size: 'lg',
        class: 'h-auto min-h-0 min-w-24 p-0 px-4 hover:bg-transparent',
      },
      {
        variant: 'simple',
        disabled: true,
        class: 'bg-transparent',
      },
    ],
  }
);

const labelsVariants = cva('', {
  variants: {
    size: {
      sm: 'text-sm',
      md: 'text-md',
      lg: 'text-title5',
    },
    variant: {
      default: 'font-medium',
      simple: 'text-md',
    },
    disabled: {
      true: 'opacity-70',
    },
    isChecked: {
      true: '',
      false: '',
    },
    isFontBold: {
      true: '',
      false: '',
    },
    withIndicator: {
      true: '',
      false: '',
    },
    as: {
      radio: '',
      checkbox: '',
    },
  },
  compoundVariants: [
    {
      isChecked: true,
      size: 'lg',
      variant: 'default',
      class: 'text-primary',
    },
    {
      withIndicator: false,
      class: 'text-center',
    },
    {
      variant: 'simple',
      isFontBold: true,
      class: 'font-medium',
    },
    {
      variant: 'simple',
      isFontBold: false,
      class: 'font-normal',
    },
  ],
});

const helpersVariants = cva('font-normal text-muted-foreground', {
  variants: {
    size: {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
    withIndicator: {
      true: '',
      false: '',
    },
    as: {
      radio: '',
      checkbox: '',
    },
    variant: {
      default: '',
      simple: '',
    },
  },
  compoundVariants: [
    {
      withIndicator: false,
      class: 'text-center',
    },
    {
      variant: 'simple',
      class: 'text-xs',
    },
  ],
});

type OptionSelectorProps = {
  label: string;
  helper?: string;
  className?: string;
} & (
  | {
      variant: 'simple';
      isFontBold?: boolean;
      size?: never;
    }
  | {
      variant?: 'default';
      size?: (typeof optionSelectorSizes)[number];
      isFontBold?: never;
    }
) &
  (CheckboxOptionSelectorProps | RadioOptionSelectorProps);

export const OptionSelector = ({
  disabled,
  label,
  helper,
  isFontBold = true,
  variant = 'default',
  size = 'md',
  className,
  ...props
}: OptionSelectorProps) => {
  const isChecked = props.checked === true;

  return (
    <Label
      className={cn(
        variants({ disabled, variant, size }),
        {
          'justify-center': props.as === 'radio' && !props.withIndicator,
        },
        className
      )}
    >
      {props.as === 'checkbox' ? (
        <Checkbox
          className={cn('mr-3', {
            'mt-0.5': variant === 'simple',
            'mt-1': variant === 'default' && size === 'lg',
          })}
          disabled={disabled}
          variant="default"
          {...props}
        />
      ) : (
        <RadioGroupItem
          checked={props.checked}
          className={cn({
            'sr-only': !props.withIndicator,
            'mr-3': props.withIndicator,
            'mt-0.5': props.withIndicator && variant === 'simple',
            'mt-1': variant === 'default' && size === 'lg',
          })}
          disabled={disabled}
          required={props.required}
          value={props.value}
        />
      )}
      <div
        className={cn('flex flex-col', {
          'gap-1': size === 'lg' && variant === 'default',
        })}
      >
        <Typography
          className={labelsVariants({
            disabled,
            size,
            variant,
            isChecked,
            isFontBold,
            withIndicator: props.withIndicator ?? true,
            as: props.as,
          })}
        >
          {label}
        </Typography>
        {!!helper && size !== 'sm' && (
          <Typography
            className={helpersVariants({
              size,
              withIndicator: props.withIndicator ?? true,
              as: props.as,
              variant,
            })}
          >
            {helper}
          </Typography>
        )}
      </div>
    </Label>
  );
};
